import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { crmLiteAllowedRoutes } from "../../Constants/CrmLiteConstants";
import { useCrmLiteClient } from "../Slices/SelectedClientSlice";
import { IFeatureToggles } from "../../Typings/FeatureToggles";

/**
 * `UseTemporaryCrmLiteRedirect` is a custom hook that temporarily handles redirection for CRM Lite clients.
 *
 * If the selected client has the Careers product but not the CRM product,
 * it redirects the user to the homepage ("/") during CRM-501 development.
 *
 * Uses the `enableAbacFeatures` flag to consider the ABAC feature toggle when determining if a route is allowed.
 *
 * @example
 * ```
 * import { UseTemporaryCrmLiteRedirect } from './UseTemporaryCrmLiteRedirect';
 *
 * function MyComponent() {
 *   UseTemporaryCrmLiteRedirect();
 *   // rest of the component
 * }
 * ```
 */
export function useTemporaryCrmLiteRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCrmLiteClient = useCrmLiteClient();
  const { enableAbacFeatures } = useFlags<IFeatureToggles>();
  const isAllowedRoute = useMemo(
    () => isRouteWithAbacAllowed(enableAbacFeatures, location.pathname),
    [enableAbacFeatures, location.pathname],
  );
  useEffect(() => {
    if (isCrmLiteClient && !isAllowedRoute) {
      navigate("/");
    }
  }, [isCrmLiteClient, isAllowedRoute, navigate]);
}

export function isRouteWithAbacAllowed(enableAbacFeatures: boolean, route: string) {
  // If ABAC features are enabled, use the allowed routes array else only allow the homepage
  return enableAbacFeatures
    ? crmLiteAllowedRoutes.some(
        // Accepts exact matches and subroutes
        (crmLiteRoute) => route === crmLiteRoute || route.startsWith(crmLiteRoute + "/"),
      )
    : route === "/";
}
